import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { HOW_I_HELP } from "@/data/content";
import { useReveal } from "@/hooks/useReveal";

const PHRASE =
  "Good creative work should do more than look polished — it should make communication clearer, guide people toward action, and support the goals of the organization behind it.";
const ITALICS = ["clearer,", "action,"];

function Word({ progress, range, italic, children }) {
  const opacity = useTransform(progress, range, [0.16, 1]);
  return (
    <span className="mr-[0.26em] inline-block">
      <motion.span style={{ opacity }} className={italic ? "serif-italic" : undefined}>
        {children}
      </motion.span>
    </span>
  );
}

function ScrollFillText() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start 0.85", "start 0.3"],
  });
  const words = PHRASE.split(" ");
  return (
    <p
      ref={ref}
      data-testid="approach-philosophy"
      className="display flex flex-wrap mt-7 text-[1.9rem] sm:text-[2.9rem] leading-[1.12] text-[var(--ink)]"
    >
      {words.map((w, i) => {
        const start = i / words.length;
        const end = start + 1 / words.length;
        return (
          <Word key={i} progress={scrollYProgress} range={[start, end]} italic={ITALICS.includes(w)}>
            {w}
          </Word>
        );
      })}
    </p>
  );
}

export default function Approach() {
  const ref = useReveal();
  return (
    <section ref={ref} data-testid="approach-section" className="relative py-28 sm:py-36">
      <div className="glow-field" style={{ opacity: 0.4 }} />
      <div className="relative mx-auto max-w-[1400px] px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
        {/* philosophy */}
        <div className="lg:col-span-7 reveal">
          <span className="label">Approach</span>
          <ScrollFillText />
        </div>

        {/* how I help */}
        <div className="lg:col-span-5 reveal lg:pt-2">
          <div className="label mb-6">Where I bring value</div>
          <ul className="border-t border-[var(--line)]">
            {HOW_I_HELP.map((h, i) => (
              <li
                key={h}
                data-testid={`help-${i}`}
                className="flex items-baseline gap-4 py-4 border-b border-[var(--line)] text-[var(--ink)]"
              >
                <span className="font-mono text-xs text-[var(--accent)]">{String(i + 1).padStart(2, "0")}</span>
                <span className="text-sm sm:text-base leading-snug">{h}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}
